import {
    Component,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';
import { ScenarioCode } from '@app/core/enums/scenario-code';
import { ScenarioInterface } from '@app/core/interfaces';
import { WINDOW } from '@inshared/shared/util';
import { DirectControlService } from '@app/my-zone/services/direct-control.service';
import { InsuranceControlActionLinkModalEnum } from '@app/my-zone/shared/insurance-control/enums/insurance-control-action-link-modal.enum';
import { InsuranceControlActionLinkVersionEnum } from '@app/my-zone/shared/insurance-control/enums/insurance-control-action-link-version.enum';
import { InsuranceControlActionEnum } from '@app/my-zone/shared/insurance-control/enums/insurance-control-action.enum';
import { InsuranceControlDisplayCriteriaEnum,
} from '@app/my-zone/shared/insurance-control/enums/insurance-control-display-criteria.enum';
import { InsuranceControlItemIdEnum } from '@app/my-zone/shared/insurance-control/enums/insurance-control-item-id.enum';
import { insuranceControlGroups } from '@app/my-zone/shared/insurance-control/insurance-control-items';
import { ActionLinkData,
} from '@app/my-zone/shared/insurance-control/interfaces/insurance-control-action-link-data.interface';
import { InsuranceControlGroup,
} from '@app/my-zone/shared/insurance-control/interfaces/insurance-control-group.interface';
import { InsuranceControlItem,
} from '@app/my-zone/shared/insurance-control/interfaces/insurance-control-item.interface';
import { DeceaseModal } from '@app/my-zone/shared/modals/decease-modal/decease.modal';
import { ReplacementSalesfunnelService } from '@app/my-zone/shared/modals/replacement-salesfunnel/replacement-salesfunnel.service';
import { MotorVehiclesToReplace } from '@app/salesfunnel/interfaces';
import { SalesfunnelRoutes } from '@app/salesfunnel/routing/salesfunnel-routing.routes';
import { DownloadingModalComponent } from '@app/shared/modals/downloading/downloading-modal.component';
import { PanelExpandableComponent } from '@app/shared/ux/panel-expandable/panel-expandable.component';
import { ScenariosSelectorService } from '@app/store/my-zone/scenarios/scenarios-selector.service';
import {
    FamilyCompositionEnum,
    InsurancePolicyDataService,
    NewModal,
    PolicyProductIdEnum,
    PolicyValidityStatusCodeEnum,
    PurchasedProductInterface,
    RelationInterface,
} from 'outshared-lib';
import { Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { InsuranceIMICModalComponent } from '../modals';

/** @deprecated - insurances functionality is moved to libs/my-zone/insurances */
@Component({
    selector: 'ins-insurance-control',
    templateUrl: './insurance-control.component.html',
    styleUrls: ['./insurance-control.component.scss'],
})
export class InsuranceControlComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChildren(PanelExpandableComponent) children: QueryList<PanelExpandableComponent>;
    @Input() userLoaded: boolean;
    @Input() onDark: boolean = false;
    @Input() relation: RelationInterface;

    public insuranceControlOptionIdEnum = InsuranceControlItemIdEnum;
    public displayGroups = insuranceControlGroups;

    private purchasedProducts: PurchasedProductInterface[];
    private familyComposition: string;
    private modalRef: any = null;
    private isSJVG: boolean = false;
    private canBeReplaced: boolean = false;
    private subscriptions = new Subscription();

    constructor(
        private newModal: NewModal,
        private directControlService: DirectControlService,
        private replaceSalesFunnelService: ReplacementSalesfunnelService,
        private insurancePolicyDataService: InsurancePolicyDataService,
        private scenariosSelectorService: ScenariosSelectorService,
        private router: Router,
        @Inject(WINDOW) private window: Window,
    ) {
        this.purchasedProducts = [];
    }

    ngOnInit(): void {
        this.initPolis();
        this.initScenarios();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.relation?.currentValue) {
            this.setFamilyComposition(this.relation);
            this.initItems();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private initPolis(): void {
        this.subscriptions.add(
            this.insurancePolicyDataService.getPurchasedProducts$()
                .pipe(map(this.isValidPurchasedProduct))
                .subscribe({
                    next: (purchasedProducts: PurchasedProductInterface[]) => {
                        this.purchasedProducts = purchasedProducts;
                        this.initItems();
                    },
                }),
        );
    }

    private isValidPurchasedProduct(purchasedProducts: PurchasedProductInterface[]): PurchasedProductInterface[] {
        return purchasedProducts
            .filter((purchasedProduct: PurchasedProductInterface) =>
                purchasedProduct?.product?.validityStatusCode !== PolicyValidityStatusCodeEnum.Ended);
    }

    private initScenarios(): void {
        this.subscriptions.add(
            this.scenariosSelectorService.getAllScenarios$.pipe(
                map((scenarios: ScenarioInterface[]): ScenarioInterface[] => scenarios
                    .filter((scenario: ScenarioInterface): boolean =>
                        scenario.scenario_code === ScenarioCode.DamageFreeYears
                        || scenario.scenario_code === ScenarioCode.VehicleReplacement,
                    ),
                ),
            ).subscribe({
                next: (scenarios: ScenarioInterface[]) => {
                    scenarios?.forEach((scenario: ScenarioInterface) => {
                        if (scenario.scenario_code === ScenarioCode.DamageFreeYears) {
                            this.isSJVG = true;
                        }
                        if (scenario.scenario_code === ScenarioCode.VehicleReplacement) {
                            this.canBeReplaced = true;
                        }
                    });
                    this.initItems();
                },
            }),
        );
    }

    public clickAction(options: InsuranceControlItem): void {
        switch (options.action) {
            case InsuranceControlActionEnum.Link:
                this.clickActionLink(options);
                break;
            case InsuranceControlActionEnum.Download :
                this.clickActionDownload();
                break;
            case InsuranceControlActionEnum.Modal:
                this.clickActionModal(options);
                break;
        }
    }

    private clickActionModal(options: InsuranceControlItem): void {
        switch (options.actionLink) {
            case InsuranceControlActionLinkModalEnum.ReplaceVehicle:
                this.checkReplaceVehicles(options.productId);
                break;
            case InsuranceControlActionLinkModalEnum.DownloadInsuranceCertificate:
                this.modalRef = this.newModal.open(InsuranceIMICModalComponent);
                this.openModal(options, 'Sluiten');
                break;
            case InsuranceControlActionLinkModalEnum.ReportDeath:
                this.modalRef = this.newModal.open(DeceaseModal);
                this.openModal(options, 'Sluiten');
                break;
        }
    }

    private clickActionDownload(): void {
        const modalRef = this.newModal.open(DownloadingModalComponent);

        this.subscriptions.add(this.directControlService.downloadPolis('J')
            .pipe(map((result) => result.data))
            .subscribe({
                next: (pdf) => {
                    modalRef.close();
                    this.downloadFile(pdf);
                },
                error: () => modalRef.close(),
            }),
        );
    }

    private clickActionLink(options: InsuranceControlItem): void {
        const actionLinkData = options.actionLink as ActionLinkData;
        switch (actionLinkData.version) {

            case InsuranceControlActionLinkVersionEnum.Old:
                this.window.location.href = actionLinkData.uri;
                break;
            case InsuranceControlActionLinkVersionEnum.Fragment:
                this.router.navigate(actionLinkData.routerLink, {
                    fragment: options.actionHash,
                }).then();
                break;
            case  InsuranceControlActionLinkVersionEnum.New:
                this.router.navigate(actionLinkData.routerLink, {
                    queryParams: {
                        panel: options.actionHash,
                        scrollTo: options.actionHash,
                    }, queryParamsHandling: 'merge',
                }).then();
                break;
            case  InsuranceControlActionLinkVersionEnum.Params:
                this.router.navigate(actionLinkData.routerLink, {
                    queryParams: {
                        section: options.actionHash,
                        expand: true,
                        scroll: true,
                    },
                }).then();
        }
    }

    private setFamilyComposition(relation: RelationInterface): void {
        this.familyComposition = relation.family_composition.family_composition_code;
    }

    private productWithValidityStatusCodeExists(codes: PolicyValidityStatusCodeEnum[]): boolean {
        return this.purchasedProducts.some((purchasedProduct: PurchasedProductInterface) =>
            codes.includes(purchasedProduct?.product?.validityStatusCode));
    }

    private productWithProductIdExists(ids: PolicyProductIdEnum[]): boolean {
        return this.purchasedProducts.some((purchasedProduct: PurchasedProductInterface) =>
            ids.includes(purchasedProduct?.productId));
    }

    private productWithStartDateExists(date: string): boolean {
        return this.purchasedProducts.some((purchasedProduct: PurchasedProductInterface) =>
            date === purchasedProduct?.product?.startDate);
    }

    private familyCompositionMatch(codes: string[]): boolean {
        return codes.includes(this.familyComposition);
    }

    private initItems(): void {
        if (!this.relation) {
            return;
        }

        this.displayGroups.forEach((group: InsuranceControlGroup): void => {
            group.items.forEach((item: InsuranceControlItem): boolean => item.visible = this.getVisibility(item));
            group.visible = group.items.some((item: InsuranceControlItem): boolean => item.visible);
        });
    }

    private getVisibility(item: InsuranceControlItem): boolean {
        if (item.displayCriteria === InsuranceControlDisplayCriteriaEnum.Always) {
            if (!((item.id === InsuranceControlItemIdEnum.MoveHouse && this.relation.residential_address_future)
                || ((
                    item.id === InsuranceControlItemIdEnum.Marrage ||
                    item.id === InsuranceControlItemIdEnum.CoInsureChildren ||
                    item.id === InsuranceControlItemIdEnum.LivingTogether ||
                    item.id === InsuranceControlItemIdEnum.BreakUp
                ) && this.relation.family_composition_future))) {
                return true;
            }
        }

        // Minimaal 1 verzekering met geldigheid_status_code active or disbarred
        if (item.displayCriteria === InsuranceControlDisplayCriteriaEnum.StatusCode &&
            this.productWithValidityStatusCodeExists([
                PolicyValidityStatusCodeEnum.Active,
                PolicyValidityStatusCodeEnum.Disbarred,
            ])
        ) {
            return true;
        }

        // Indien een actieve Auto
        if (item.displayCriteria === InsuranceControlDisplayCriteriaEnum.ActiveCar &&
            this.productWithProductIdExists([PolicyProductIdEnum.Car]) &&
            this.canBeReplaced) {
            return true;
        }

        // Indien een actieve Motor
        if (item.displayCriteria === InsuranceControlDisplayCriteriaEnum.ActiveMotor &&
            this.productWithProductIdExists([PolicyProductIdEnum.Motorcycle]) &&
            this.canBeReplaced) {
            return true;
        }

        if ((item.id === InsuranceControlItemIdEnum.BreakUp
                || item.id === InsuranceControlItemIdEnum.CoInsureChildren
                || item.id === InsuranceControlItemIdEnum.LivingTogether)
            && !this.relation.family_composition_future) {
            return (item.displayCriteria === InsuranceControlDisplayCriteriaEnum.Family1ZON2ZON
                    && this.familyCompositionMatch([
                        FamilyCompositionEnum.SinglePerson,
                        FamilyCompositionEnum.TwoPersons,
                    ]))
                || (item.displayCriteria === InsuranceControlDisplayCriteriaEnum.Family1ZON1MET
                    && this.familyCompositionMatch([
                        FamilyCompositionEnum.SinglePerson,
                        FamilyCompositionEnum.SinglePersonWithChildren,
                    ]))
                || (item.displayCriteria === InsuranceControlDisplayCriteriaEnum.Family1MET2ZON2MET
                    && this.familyCompositionMatch([
                        FamilyCompositionEnum.SinglePersonWithChildren,
                        FamilyCompositionEnum.TwoPersons,
                        FamilyCompositionEnum.TwoPersonsWithChildren,
                    ]));
        }

        // Indien minimaal: Auto, Motor, Brommer of Caravan
        if (item.displayCriteria === InsuranceControlDisplayCriteriaEnum.CarMotorMopedCaravan &&
            this.productWithProductIdExists([
                PolicyProductIdEnum.Car,
                PolicyProductIdEnum.Moped,
                PolicyProductIdEnum.Motorcycle,
                PolicyProductIdEnum.Caravan,
            ])) {
            return true;
        }

        // Indien minimaal: Auto, Motor, Brommer
        if (item.displayCriteria === InsuranceControlDisplayCriteriaEnum.CarMotorMoped &&
            this.productWithProductIdExists([
                PolicyProductIdEnum.Car,
                PolicyProductIdEnum.Moped,
                PolicyProductIdEnum.Motorcycle,
            ])) {
            return true;
        }

        // Indien ingangsdatum: 9999-12-31
        if (item.displayCriteria === InsuranceControlDisplayCriteriaEnum.Date99991231 &&
            this.productWithStartDateExists('9999-12-31')) {
            return true;
        }

        // Schadevrije jaren
        if (item.displayCriteria === InsuranceControlDisplayCriteriaEnum.NoClaimYears &&
            this.isSJVG) {
            return true;
        }

        return false;

    }

    private openModal(options: InsuranceControlItem, confirmation: string): void {
        if (this.modalRef) {
            this.modalRef.componentInstance.model = {
                product_id: options.productId,
                title: options.name,
                bevestiging: confirmation,
                ontkenning: 'Annuleren',
            };
        }
    }

    private checkReplaceVehicles(productId: PolicyProductIdEnum): void {
        this.replaceSalesFunnelService
            .getReplacementVehicles$(productId)
            .pipe(take(1))
            .subscribe({
                next: (products: MotorVehiclesToReplace) => {
                    if (products.purchased_product_replacement?.length) {
                        // TODO: WINS-7638 refactor replaceVehicleModal
                        // this.insuranceModalService.replaceVehicleModal(productId);
                        return;
                    }
                    if (productId === PolicyProductIdEnum.Car) {
                        this.directControlService
                            .link(`/${SalesfunnelRoutes.CarInsurance}/${SalesfunnelRoutes.CalculateCarInsurance}`);
                        return;
                    }
                    if (productId === PolicyProductIdEnum.Motorcycle) {
                        this.directControlService
                            .link(`/${SalesfunnelRoutes.MotorInsurance}/${SalesfunnelRoutes.CalculateMotorInsurance}`);
                        return;
                    }
                },
            },
);
    }

    private downloadFile({pdf}: { pdf: string }): void {
        if (pdf) {
            this.window.location.href = `/documenten/download/${pdf}`;
        } else {
            console.warn('Helaas geen PDF kunnen ophalen');
        }
    }
}
